/**
 * Top-level gate (ARCHITECTURE §9). Resolves the session, then renders the login, the PENDING
 * waiting screen, the admin console, or the crew mission app. The mission runtime is created only
 * once the device is APPROVED, with the server-provided identity.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { getSession, type Session } from './api.js';
import { initRuntime } from '../runtime/runtime.js';
import { setupServiceWorker } from '../app/pwa.js';
import { App } from '../app/App.js';
import { Login } from './Login.js';
import { PendingScreen } from './Pending.js';
import { AdminConsole } from '../admin/AdminConsole.js';

type Phase = { k: 'loading' } | { k: 'login' } | { k: 'pending'; session: Session } | { k: 'crew'; session: Session } | { k: 'admin'; session: Session };

function bootId(): string {
  return `b-${Math.random().toString(36).slice(2, 10)}`;
}

export function AuthGate(): ReactNode {
  const [phase, setPhase] = useState<Phase>({ k: 'loading' });

  const resolve = useCallback(async () => {
    const session = await getSession();
    if (!session) return setPhase({ k: 'login' });
    if (session.pending) return setPhase({ k: 'pending', session });
    if (session.role === 'ADMIN') return setPhase({ k: 'admin', session });
    // create the mission runtime with the server identity BEFORE rendering App, so App's
    // useRuntime() → getRuntime() never falls back to a default identity (ordering matters).
    const rt = initRuntime({ role: session.role, user: session.user, deviceId: session.deviceId, bootId: bootId() });
    rt.start();
    setupServiceWorker(rt);
    setPhase({ k: 'crew', session });
  }, []);

  useEffect(() => {
    void resolve();
  }, [resolve]);

  switch (phase.k) {
    case 'loading':
      return <div className="auth-splash">Connecting…</div>;
    case 'login':
      return <Login onAuthenticated={resolve} />;
    case 'pending':
      return <PendingScreen session={phase.session} onApproved={resolve} />;
    case 'admin':
      return <AdminConsole session={phase.session} />;
    case 'crew':
      return <App />;
  }
}
